import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  Calendar,
  CheckCircle,
  Clock,
  DollarSign,
  FileSignature,
  FileText,
  Link2,
  Loader2,
  Lock,
  Plus,
  Wallet,
} from 'lucide-react';
import api from '../../services/authService';
import { vendorExternalLeadApi } from '../../services/vendorExternalLeadService';
import { VendorPageHeader } from '../../components/vendor/guidance';
import ChannelBadge from '../../components/shared/ChannelBadge';
import { formatDate } from '../../utils/dateUtils';
import { formatDisplayMoney } from '../../utils/formatMoney';

const VendorExternalDashboard = () => {
  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['vendorBookingStats', 'external'],
    queryFn: async () => {
      const response = await api.get('/vendor/bookings/statistics', {
        params: { channel: 'external' },
      });
      return response.data;
    },
    staleTime: 15_000,
    refetchInterval: 30_000,
  });

  const { data: bookingsData, isLoading: bookingsLoading } = useQuery({
    queryKey: ['vendorRecentExternalBookings'],
    queryFn: async () => {
      const response = await api.get(
        '/vendor/bookings?per_page=5&sort_by=created_at&sort_order=desc&channel=external'
      );
      return response.data;
    },
    staleTime: 15_000,
    refetchInterval: 30_000,
  });

  const { data: leadsData, isLoading: leadsLoading } = useQuery({
    queryKey: ['vendorExternalLeads', { status: 'pending', per_page: 5 }],
    queryFn: () =>
      vendorExternalLeadApi.listLeads({ status: 'pending', per_page: 5 }),
    staleTime: 15_000,
    refetchInterval: 30_000,
  });

  const { data: invoicesData, isLoading: invoicesLoading } = useQuery({
    queryKey: ['vendorExternalInvoiceCount'],
    queryFn: async () => {
      const response = await api.get('/vendor/invoices', {
        params: { channel: 'external', per_page: 1 },
      });
      return response.data;
    },
    staleTime: 30_000,
  });

  const { data: contractsData, isLoading: contractsLoading } = useQuery({
    queryKey: ['vendorContractCount'],
    queryFn: async () => {
      const response = await api.get('/vendor/contracts', {
        params: { per_page: 1 },
      });
      return response.data;
    },
    staleTime: 30_000,
  });

  const stats = statsData?.data || {};
  const recentBookings = bookingsData?.data?.data || bookingsData?.data || [];
  const newLeads = leadsData?.data?.data || leadsData?.data || [];
  const leadCounts = leadsData?.counts || {};
  const invoiceTotal =
    invoicesData?.data?.total ??
    invoicesData?.data?.meta?.total ??
    invoicesData?.meta?.total ??
    0;
  const contractTotal =
    contractsData?.data?.total ??
    contractsData?.data?.meta?.total ??
    contractsData?.meta?.total ??
    0;

  const money = (n) => formatDisplayMoney(n || 0, 'USD', 'en-US');
  const loading = statsLoading || leadsLoading || invoicesLoading || contractsLoading;

  const cards = [
    {
      label: 'Total bookings',
      value: stats.total_bookings ?? stats.external_bookings ?? 0,
      hint: 'All external / private jobs',
      to: '/vendor/private-bookings',
      Icon: Lock,
    },
    {
      label: 'Completed',
      value: stats.completed_bookings ?? 0,
      hint: 'Finished external events',
      to: '/vendor/private-bookings',
      Icon: CheckCircle,
    },
    {
      label: 'Upcoming',
      value:
        (stats.pending_bookings || 0) +
        (stats.confirmed_bookings || 0) +
        (stats.in_progress_bookings || 0),
      hint: 'Pending · confirmed · in progress',
      to: '/vendor/private-bookings',
      Icon: Clock,
    },
    {
      label: 'Collected',
      value: money(stats.external_offline_collected ?? stats.private_offline_collected ?? 0),
      hint: 'Offline payments recorded',
      to: '/vendor/private-bookings',
      Icon: DollarSign,
      isAmount: true,
    },
    {
      label: 'Balance due',
      value: money(stats.external_balance_due ?? 0),
      hint:
        Number(stats.external_discount_total) > 0
          ? `After discounts (−${money(stats.external_discount_total)})`
          : 'Still owed after discounts',
      to: '/vendor/private-bookings',
      Icon: Wallet,
      isAmount: true,
    },
    {
      label: 'New leads',
      value: leadCounts.pending ?? leadCounts.new ?? newLeads.length ?? 0,
      hint: `Inbox · ${leadCounts.total ?? 0} total leads`,
      to: '/vendor/external-leads',
      Icon: Link2,
    },
    {
      label: 'Guest invoices',
      value: invoiceTotal,
      valueLoading: invoicesLoading,
      hint: 'External channel invoices',
      to: '/vendor/invoices',
      Icon: FileText,
    },
    {
      label: 'Contracts',
      value: contractTotal,
      valueLoading: contractsLoading,
      hint: 'Signed & draft contracts',
      to: '/vendor/contracts',
      Icon: FileSignature,
    },
  ];

  return (
    <div className="space-y-6 pb-10">
      <VendorPageHeader
        title="External overview"
        subtitle="Your own clients — leads, private bookings, guest invoices, and contracts. Outside marketplace fees and payouts."
        actions={
          <>
            <ChannelBadge channel="external" />
            <Link
              to="/vendor/private-bookings"
              className="inline-flex items-center gap-1.5 rounded-md bg-ink px-3 py-1.5 text-xs font-medium text-white hover:bg-neutral-800"
            >
              <Plus className="h-3.5 w-3.5" />
              Private booking
            </Link>
          </>
        }
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map(({ label, value, hint, to, Icon, valueLoading }) => (
          <Link
            key={label}
            to={to}
            className="rounded-product-md border border-border bg-surface p-4 transition-colors hover:bg-surface-muted"
          >
            <div className="mb-2 flex items-center gap-2">
              <Icon className="h-4 w-4 text-ink-tertiary" />
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-tertiary">
                {label}
              </p>
            </div>
            {loading || valueLoading ? (
              <Loader2 className="h-6 w-6 animate-spin text-ink-tertiary" />
            ) : (
              <p className="text-2xl font-semibold tabular-nums text-ink">{value}</p>
            )}
            <p className="mt-1 text-xs text-ink-secondary">{hint}</p>
          </Link>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        {[
          { to: '/vendor/external-leads', label: 'External leads', Icon: Link2 },
          { to: '/vendor/private-bookings', label: 'Private bookings', Icon: Lock },
          { to: '/vendor/invoices', label: 'Guest invoices', Icon: FileText },
          { to: '/vendor/contracts', label: 'Contracts', Icon: FileSignature },
          { to: '/vendor/calendar', label: 'Calendar', Icon: Calendar },
        ].map(({ to, label, Icon }) => (
          <Link
            key={to}
            to={to}
            className="inline-flex items-center gap-1.5 rounded-product-md border border-border bg-surface px-3 py-1.5 text-xs font-medium text-ink transition-colors hover:bg-surface-muted"
          >
            <Icon className="h-3.5 w-3.5 text-ink-tertiary" />
            {label}
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6">
        <section className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold tracking-tight text-ink">
              Recent private bookings
            </h2>
            <Link
              to="/vendor/private-bookings"
              className="inline-flex items-center gap-1 text-sm font-medium text-ink"
            >
              View all <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="product-surface overflow-hidden">
            {bookingsLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-ink" />
              </div>
            ) : recentBookings.length === 0 ? (
              <div className="px-6 py-12 text-center text-sm text-ink-secondary">
                No private bookings yet.{' '}
                <Link
                  to="/vendor/private-bookings"
                  className="font-medium text-ink underline-offset-2 hover:underline"
                >
                  Add one
                </Link>
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {recentBookings.map((b) => (
                  <li key={b.id}>
                    <Link
                      to={`/vendor/bookings/${b.id}`}
                      className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-surface-muted"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-ink">
                          {b.guest_name || b.manual_title || b.booking_reference}
                        </p>
                        <p className="text-xs text-ink-tertiary">
                          {b.event_date ? formatDate(b.event_date, 'short') : 'No date'} · #
                          {b.booking_reference}
                        </p>
                      </div>
                      <ChannelBadge channel="external" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>

        <section className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold tracking-tight text-ink">New leads</h2>
            <Link
              to="/vendor/external-leads"
              className="inline-flex items-center gap-1 text-sm font-medium text-ink"
            >
              Inbox <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="product-surface overflow-hidden">
            {leadsLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-ink" />
              </div>
            ) : newLeads.length === 0 ? (
              <div className="px-6 py-12 text-center text-sm text-ink-secondary">
                No new leads.{' '}
                <Link
                  to="/vendor/external-leads"
                  className="font-medium text-ink underline-offset-2 hover:underline"
                >
                  Create a share link
                </Link>
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {newLeads.slice(0, 5).map((lead) => (
                  <li key={lead.id}>
                    <Link
                      to={`/vendor/external-leads?lead=${lead.id}`}
                      className="block px-4 py-3 hover:bg-surface-muted"
                    >
                      <p className="text-sm font-medium text-ink">{lead.guest_name}</p>
                      <p className="text-xs text-ink-tertiary">
                        {[lead.guest_email, lead.event_date && formatDate(lead.event_date, 'short')]
                          .filter(Boolean)
                          .join(' · ') || 'New inquiry'}
                      </p>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>

      <p className="text-center text-xs text-ink-tertiary">
        Looking for marketplace metrics?{' '}
        <Link
          to="/vendor/dashboard"
          className="font-medium text-ink underline-offset-2 hover:underline"
        >
          Open Internal dashboard
        </Link>
      </p>
    </div>
  );
};

export default VendorExternalDashboard;
